import { FlexCol, FlexRow } from '@/components/common/Flex';
import SlideDownBox from '@/components/common/SlideDownBox';
import { Divider, FlexProps, forwardRef, Tooltip } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import { Button, ButtonProps } from './Button';
import TwoColumnLayout, { TwoColumnLayoutProps } from './TwoColumnLayout';
import { Text } from './Typography';

export const entityViewInset = 16;

export const EntityViewHeader = (props: FlexProps) => {
    return (
        <FlexRow
            w="full"
            py={{ base: 4, '2xl': 4 }}
            px={entityViewInset}
            justify="space-between"
            align="center"
            bg="gray.50"
            minH={{ base: 22, '2xl': 22 }}
            {...props}
        />
    );
};

interface EntityViewHeaderNotificationProps extends FlexProps {
    isVisible: boolean;
}

export const EntityViewHeaderNotification = ({
    isVisible,
    ...props
}: EntityViewHeaderNotificationProps) => {
    return (
        <AnimatePresence>
            {isVisible && (
                <SlideDownBox
                    pos="fixed"
                    top={0}
                    left={0}
                    zIndex={300}
                    w="full"
                >
                    <EntityViewHeader {...props} />
                </SlideDownBox>
            )}
        </AnimatePresence>
    );
};

export const EntityViewContent = (props: FlexProps) => {
    return <FlexCol basis={0} grow={1} overflow="auto" pt={12} {...props} />;
};

export type EntityViewGridProps = TwoColumnLayoutProps;
export const EntityViewGrid = (props: EntityViewGridProps) => {
    return (
        <TwoColumnLayout
            {...props}
            gap={entityViewInset}
            px={entityViewInset}
        />
    );
};

export interface EntityViewGridSectionProps extends Omit<FlexProps, 'title'> {
    title?: React.ReactNode;
    headerRight?: JSX.Element;
    headerProps?: FlexProps;
}

export const EntityViewGridSection = ({
    title,
    children,
    headerRight,
    headerProps = {},
    ...props
}: EntityViewGridSectionProps) => {
    return (
        <FlexCol {...props}>
            {title && (
                <FlexRow
                    align="center"
                    justify="space-between"
                    mb={8}
                    {...headerProps}
                >
                    <Text fontWeight="medium" fontSize="md">
                        {title}
                    </Text>
                    {headerRight}
                </FlexRow>
            )}
            {children}
        </FlexCol>
    );
};

export const EntityViewActionButton = forwardRef(
    (
        {
            label,
            tooltipLabel,
            icon,
            ...props
        }: ButtonProps & {
            label: string;
            tooltipLabel?: string;
            icon: JSX.Element;
        },
        ref
    ) => {
        const button = (
            <Button
                ref={ref}
                variant="ghost"
                colorScheme="gray"
                rounded="md"
                h={16}
                w={16}
                p={2}
                {...props}
            >
                <FlexCol justify="center" align="center" color="text">
                    {icon}
                    <Text mt={2} color="gray.400" fontSize="sm">
                        {label}
                    </Text>
                </FlexCol>
            </Button>
        );

        return tooltipLabel ? (
            <Tooltip placement="top" label={tooltipLabel}>
                {button}
            </Tooltip>
        ) : (
            button
        );
    }
);

export const EntityViewDivider = (props: FlexProps) => {
    return (
        <FlexRow p={entityViewInset} {...props}>
            <Divider />
        </FlexRow>
    );
};
